import { AxiosError } from "axios"
import { LoversLiveForeverLogo } from "components/LoversLiveForeverLogo"
import { Alert, Button, TextField } from "components/ui"
import { useAuth } from "hooks/useAuth"
import { useState } from "react"
import { Link } from "react-router-dom"

export const SignupPage = () => {
  const { signUp } = useAuth()
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [agreeToTerms, setAgreeToTerms] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!agreeToTerms) {
      setError("You must agree to the Terms and Privacy Policy")
      return
    }

    setError(null)
    setIsLoading(true)

    try {
      await signUp({ name, email, password })
    } catch (err: unknown | AxiosError) {
      if (err instanceof AxiosError) {
        setError(err.response?.data?.errorMessage || "Failed to sign up")
      } else {
        setError("Failed to sign up")
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Link to="/">
            <LoversLiveForeverLogo className="mx-auto h-20 w-auto" />
          </Link>
          <h2 className="mt-8 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white">
            Create a new account
          </h2>
        </div>

        <div className="sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white dark:bg-gray-900 px-6 py-12 sm:px-12">
            {error && (
              <Alert variant="error" className="mb-4">
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <TextField
                label="Name"
                id="name"
                name="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
              />

              <TextField
                label="Email address"
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />

              <TextField
                label="Password"
                id="password"
                name="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />

              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="terms"
                    name="terms"
                    type="checkbox"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 dark:border-gray-700 text-primary-600 focus:ring-primary-600"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="terms" className="text-gray-600 dark:text-gray-300">
                    I agree to the{" "}
                    <Link
                      to="/terms"
                      className="font-semibold text-primary-600 hover:text-primary-500"
                      target="_blank"
                    >
                      Terms and Agreements
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="/privacy-policy"
                      className="font-semibold text-primary-600 hover:text-primary-500"
                      target="_blank"
                    >
                      Privacy Policy
                    </Link>
                  </label>
                </div>
              </div>

              <div>
                <Button
                  type="submit"
                  fullWidth
                  isLoading={isLoading}
                  disabled={!agreeToTerms}
                >
                  {isLoading ? "Creating your account..." : "Create new account"}
                </Button>
              </div>
            </form>
          </div>

          <p className="text-center text-sm/6 text-gray-500 dark:text-gray-400">
            Already a member?{" "}
            <Link
              to="/login"
              className="font-semibold text-primary-600 hover:text-primary-500"
            >
              Log in instead! ❤️
            </Link>
          </p>
        </div>
      </div>
    </>
  )
}
